import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle2, MessageSquare, Eye, CalendarClock, Send, Loader2 } from "lucide-react";
import { getReviewQueue, approveListing, requestChanges, scheduleListing, publishListing } from "@/lib/api";
import { statusLabel, statusTone } from "@/lib/status";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { MobileMenu, MobileTabBar } from "@/components/AppNav";
import { AppHeader } from "@/components/brand/AppHeader";
import { AdStatusPill } from "@/components/AdTemplateStatus";
import { TestBadge } from "@/components/TestMode";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

export default function ReviewQueue() {
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [noteFor, setNoteFor] = useState(null);
  const [note, setNote] = useState("");

  const load = async () => {
    try { setRows(await getReviewQueue()); } catch { setRows([]); }
  };
  useEffect(() => { load(); }, []);

  const act = async (id, fn, okMsg) => {
    setBusyId(id);
    try { const r = await fn(id); toast.success(okMsg === "Published" && r?.listing_status === "scheduled" ? "Scheduled — goes live on its Listing Date" : okMsg); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Action failed"); }
    finally { setBusyId(null); }
  };

  const sendChanges = async () => {
    if (!note.trim()) return toast.error("Add a note for the seller");
    const id = noteFor;
    setNoteFor(null);
    await act(id, (i) => requestChanges(i, note.trim()), "Changes requested — seller notified");
    setNote("");
  };

  return (
    <div className="min-h-screen bg-[#FAF8F5] pb-24 md:pb-0 overflow-x-clip">
      <AppHeader title="Review Queue" onBack={() => navigate("/")} backTestId="back-to-horses" width="max-w-5xl">
        <MobileMenu />
      </AppHeader>

      <main className="max-w-5xl mx-auto px-4 sm:px-8 py-5 md:py-8">
        {rows === null ? (
          <div className="space-y-3">{[1, 2].map((i) => <Skeleton key={i} className="h-24 w-full rounded-xl" />)}</div>
        ) : rows.length === 0 ? (
          <div data-testid="review-empty" className="rounded-2xl border border-dashed border-stone-300 bg-white p-10 text-center">
            <p className="font-medium text-stone-800">Nothing to review right now.</p>
            <p className="text-sm text-stone-500 mt-1">Submitted seller listings will appear here.</p>
          </div>
        ) : (
          <div data-testid="review-list" className="space-y-3">
            {rows.map((h) => (
              <div key={h.id} data-testid={`review-row-${h.id}`} className="bg-white border border-stone-200 rounded-xl p-4 md:p-5">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-semibold text-stone-900 break-words flex items-center gap-1.5">{h.is_test && <TestBadge testId={`review-test-${h.id}`} />}{h.horse_info?.horse_name || "Untitled horse"}{h.registration_review?.needed && <span data-testid={`review-reg-flag-${h.id}`} title={h.registration_review.reason} className="text-[10px] font-semibold rounded-full border border-amber-300 bg-amber-50 text-amber-900 px-2 py-0.5">Check registration papers</span>}</div>
                    {h.application_id && <div className="mt-1"><AdStatusPill status={h.ad_template_status} testId={`review-ad-status-${h.id}`} prefix="Horse Ad Template: " /></div>}
                    <div className="text-xs text-stone-500 mt-0.5 break-all">{h.owner_email}</div>
                    {h.review_note && (
                      <div className="text-xs text-amber-800 mt-1">Last note: {h.review_note}</div>
                    )}
                  </div>
                  <Badge variant="outline" className={`rounded-full shrink-0 ${statusTone(h.listing_status)}`}>
                    {statusLabel(h.listing_status)}
                  </Badge>
                </div>
                <div className="grid grid-cols-2 sm:flex sm:flex-wrap items-center gap-2 mt-4 [&>button]:h-11 sm:[&>button]:h-9">
                  <Button data-testid={`review-open-${h.id}`} size="sm" variant="outline"
                    onClick={() => navigate(`/horse/${h.id}`)}
                    className="rounded-full border-stone-300 text-stone-700">
                    <Eye className="h-4 w-4 mr-1.5" /> Open
                  </Button>
                  {h.application_id && (
                    <Button data-testid={`review-ad-template-${h.id}`} size="sm" variant="outline" onClick={() => navigate(`/listing/${h.id}`)}
                      className="rounded-full border-stone-300 text-stone-700">
                      <Eye className="h-4 w-4 mr-1.5" /> Ad Template &amp; Papers
                    </Button>
                  )}
                  <Button data-testid={`review-changes-${h.id}`} size="sm" variant="outline"
                    onClick={() => { setNoteFor(h.id); setNote(h.review_note || ""); }}
                    className="rounded-full border-amber-300 text-amber-800 hover:bg-amber-50">
                    <MessageSquare className="h-4 w-4 mr-1.5" /> Request Changes
                  </Button>
                  <Button data-testid={`review-approve-${h.id}`} size="sm"
                    disabled={busyId === h.id || !["submitted", "changes_requested"].includes(h.listing_status)}
                    onClick={() => act(h.id, approveListing, "Approved")}
                    className="rounded-full bg-primary hover:bg-amber-900 text-white">
                    {busyId === h.id ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-1.5" />}
                    Approve
                  </Button>
                  <Button data-testid={`review-schedule-${h.id}`} size="sm" variant="outline"
                    disabled={busyId === h.id || h.listing_status !== "approved"}
                    onClick={() => act(h.id, scheduleListing, "Scheduled")}
                    className="rounded-full border-violet-300 text-violet-700 hover:bg-violet-50">
                    <CalendarClock className="h-4 w-4 mr-1.5" /> Schedule
                  </Button>
                  <Button data-testid={`review-publish-${h.id}`} size="sm"
                    disabled={busyId === h.id || !["approved", "scheduled"].includes(h.listing_status)}
                    onClick={() => act(h.id, publishListing, "Published")}
                    className="rounded-full bg-amber-800 hover:bg-amber-900 text-white">
                    <Send className="h-4 w-4 mr-1.5" /> Publish
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
      <MobileTabBar />

      <Dialog open={!!noteFor} onOpenChange={(o) => !o && setNoteFor(null)}>
        <DialogContent data-testid="request-changes-dialog" className="w-[calc(100%-1.5rem)] rounded-xl">
          <DialogHeader><DialogTitle>Request changes from seller</DialogTitle></DialogHeader>
          <Textarea data-testid="request-changes-note" rows={4} value={note} onChange={(e) => setNote(e.target.value)}
            placeholder="Tell the seller what to fix (e.g. add more photos, confirm the price)."
            className="border-stone-200 focus-visible:ring-amber-800" />
          <DialogFooter className="gap-2">
            <Button data-testid="cancel-changes-button" variant="outline" onClick={() => setNoteFor(null)} className="h-11 sm:h-10 rounded-full">Cancel</Button>
            <Button data-testid="send-changes-button" onClick={sendChanges}
              className="h-11 sm:h-10 rounded-full bg-amber-800 hover:bg-amber-900 text-white">Send to Seller</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
